<template>
  <div class="page-content">
    <div class="payment">
      <div class="payment-header">
        <CustomIcon name="logo" class="payment-header-logo" />
        <h2 class="payment-title">Get started</h2>
      </div>
      <h3 class="payment-subtitle">Set Up Your Account</h3>
      <FieldInput
        name="name"
        placeholder="Full name (must match the claim details)"
        :value="formData.name.value"
        :touched="formData.name.touched"
        :onBlur="() => updateForm('name', formData.name.value, true)"
        :onChange="(event) => updateForm('name', event.target.value, true)"
        :validator="(value) => validator['name'](value)"
      />
      <FieldInput
        name="email"
        placeholder="Email"
        :value="formData.email.value"
        :touched="formData.email.touched"
        :onBlur="() => updateForm('email', formData.email.value, true)"
        :onChange="(event) => updateForm('email', event.target.value, true)"
        :validator="(value) => validator['email'](value)"
      />
      <p class="payment-text">
        oVRcome will use this email to authenticate log-ins, ensure you have
        access to it on your mobile phone.
      </p>

      <div class="payment-checkbox-div">
        <input
          class="payment-checkbox-style"
          type="checkbox"
          v-model="isDataCollectionApproved"
        />
        <span class="payment-checkbox-text"
          >I consent for oVRcome to collect data on the responses I provide pre
          and post each module, and to share it with Allianz.
          <a href="#" @click.prevent="openModal">Learn more</a></span
        >
      </div>

      <ModalComponent :show="modalVisible" @close="closeModal">
        <!-- Modal content goes here -->
        <h4 class="payment-subtitle">Set Up Your Account</h4>
        <p class="payment-text">Collection of personal and other information</p>
        <p class="payment-checkbox-text">
          oVRcome may collect personal information and other data about You from
          Your log in to the Fear of Driving program and as you work through the
          driving modules. The types of information collected will be:
        </p>
        <p class="payment-checkbox-text">- email address</p>
        <p class="payment-checkbox-text">- name</p>
        <p class="payment-checkbox-text">
          - progress as your work through each module
        </p>
        <p class="payment-checkbox-text">- level of anxiety post each module</p>
        <p class="payment-checkbox-text">- log in duration for the app</p>
        <p class="payment-checkbox-text">- modules accessed and time taken</p>
        <p class="payment-checkbox-text">
          This personal information may be shared with Allianz so that they may
          review how the program is being used, the outcomes for our customers
          with overcoming their fear of driving and also for any program
          improvements that may be needed.
        </p>
        <p class="payment-checkbox-text">
          <a href="https://www.ovrcome.io/privacy-policy" target="_blank"
            >Privacy Policy</a
          >
        </p>
      </ModalComponent>

      <div class="payment-checkbox-div">
        <input
          class="payment-checkbox-style"
          type="checkbox"
          v-model="isGpSupportApproved"
          @change="onCheckboxChange"
        />
        <span class="payment-checkbox-text"
          >I confirm that I have approval from my GP and Allianz to attend the
          self-guided exposure program.</span
        >
      </div>

      <SnackbarPrompt v-if="errorMessage" icon="warning" variation="warning">
        <div class="payment-snackbar">
          <p class="payment-snackbar-title-text" v-if="!isGpSupportApproved">
            Approval from GP and Allianz is required.
          </p>
          {{ errorMessage }}
        </div>
      </SnackbarPrompt>
      <CustomButton
        :disabled="!formIsValid() || registerUserIsLoading"
        :onClick="(e) => handleRegisterUser(e)"
      >
        <span v-if="!registerUserIsLoading">Continue</span>
        <LoadingIndicator v-else />
      </CustomButton>
    </div>
  </div>
</template>

<script>
import { EXTERNAL_ROUTES, PAGE_PATH, MODULES, PLAN_TYPES } from "@/config";
import { webPaymentSetupAccountValidator } from "@/utils/form-validation/index";
import { getPlansParams } from "@/utils/get-plans-params";
import { pageInformationMixin } from "@/mixins/index.js";
import { denormaliseOrderSummary, getCourseType } from "@/utils";
import CustomIcon from "@/components/CustomIcon.vue";
import FieldInput from "@/components/FieldInput.vue";
import CustomButton from "@/components/CustomButton.vue";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import SnackbarPrompt from "@/components/SnackbarPrompt.vue";
import moment from "moment";
import { mapGetters } from "vuex";
import ModalComponent from "@/components/ModalComponent.vue";

export default {
  data() {
    return {
      validator: webPaymentSetupAccountValidator,
      terms: EXTERNAL_ROUTES.termsAndConditions,
      privacyPolicy: EXTERNAL_ROUTES.privacyPolicy,
      registerUserIsLoading: false,
      errorMessage: null,
      isDataCollectionApproved: false,
      isGpSupportApproved: false,
      modalVisible: false,
    };
  },
  props: {
    updateFormState: Function,
    action: Function,
    formData: Object,
  },
  components: {
    CustomIcon,
    FieldInput,
    CustomButton,
    LoadingIndicator,
    SnackbarPrompt,
    ModalComponent,
  },
  mixins: [pageInformationMixin],
  computed: {
    ...mapGetters(MODULES.WELCOME, {
      onboardingData: "getOnboardingData",
    }),
    ...mapGetters(MODULES.PLAN, {
      availablePlans: "getAvailablePlans",
    }),
    ...mapGetters(MODULES.ANXIETY, {
      anxietyData: "getAnxietyData",
    }),
  },
  mounted() {
    const { dispatch } = this.$store;
    const { formData, onboardingData } = this;

    dispatch(
      `${MODULES.PLAN}/getPlansAction`,
      getPlansParams({ onboardingData })
    ).then(() => {
      if (!formData.plan_id.value) {
        this.updateFormState("plan_id", this.getPlans()[0]?.id, false);
      }
    });
    this.trackEmail();
  },
  methods: {
    openModal() {
      this.modalVisible = true;
    },
    closeModal() {
      this.modalVisible = false;
    },
    updateForm(key, value, touched = false) {
      const { updateFormState } = this;

      updateFormState(key, value, touched);
    },
    onCheckboxChange() {
      if (!this.isGpSupportApproved) {
        this.errorMessage =
          "Thanks for your interest in participating in the VR program to overcome your fear of driving, please make contact with your case manager to seek further support on how you may access this support.";
      } else {
        this.errorMessage = "";
      }
    },
    formIsValid() {
      if (!this.isGpSupportApproved) {
        return false;
      }
      let keys = Object.keys(this.formData);

      const properties = (validator, key) =>
        Object.prototype.hasOwnProperty.call(validator, key);

      const formIsValid = !keys.some((key) => {
        if (properties(this.validator, key)) {
          return !this.validator[key](this.formData[key].value).valid;
        }

        return false;
      });

      return formIsValid;
    },
    getPlans() {
      const { onboardingData } = this;

      const plans = this.availablePlans;

      if (onboardingData.choose_path === PAGE_PATH.myself) {
        return plans.filter((plan) => plan.type === PLAN_TYPES.globalPlan);
      }

      if (onboardingData.choose_path === PAGE_PATH.parent) {
        return this.availablePlans;
      }
    },
    handleRegisterUser(e) {
      if (e) {
        e.preventDefault();
      }

      const { dispatch } = this.$store;
      const { choose_path, age_select, choose_phobias, choose_avatar } =
        this.onboardingData;

      this.registerUserIsLoading = true;

      if (choose_path === PAGE_PATH.parent) {
        dispatch(`${MODULES.USER}/createEvent`, {
          events: [
            {
              category_id: age_select,
              phobias: choose_phobias,
              type: "user_programme_phobias_selected",
              created_at: moment().format("YYYY/MM/DD HH:mm:ss Z"),
            },
            {
              avatar: choose_avatar?.toString(),
              type: "user_attribute_provided",
              created_at: moment().format("YYYY/MM/DD HH:mm:ss Z"),
            },
          ],
        })
          .then(() => this.registerUser())
          .catch(() => {
            this.registerUserIsLoading = false;
            this.errorMessage =
              "Something went wrong trying to set up your account. Please contact ovrcome support: support@ovrcome.io";
          });
      } else {
        this.registerUser();
      }
    },
    registerUser() {
      const { dispatch } = this.$store;
      const {
        age_select,
        choose_gender,
        choose_path,
        choose_phobias,
        name_input,
        choose_ethnicity,
        reason_select,
      } = this.onboardingData;

      const { choose_fears } = this.anxietyData;

      let payload = {
        email: this.formData.email.value,
        first_name: this.formData.name.value,
      };

      if (choose_path === PAGE_PATH.myself) {
        payload = {
          ...payload,
          category_id: choose_phobias,
          course_type: "full",
          gender: choose_gender,
          paymentType: "premium",
          details: {
            gender: choose_gender,
            age: age_select,
            ethnicity: choose_ethnicity,
            onboarding_reason_for_doing_program: reason_select,
            onboarding_what_scares_me: choose_fears,
            allianz_data_collection_approved:
              this.isDataCollectionApproved.toString(),
          },
        };
      } else if (choose_path === PAGE_PATH.parent) {
        payload = {
          ...payload,
          category_id: age_select,
          course_type: "childrens",
          first_name: name_input,
          gender: choose_gender,
          paymentType: "parent",
          source: "parent_via_app",
        };
      }

      dispatch(`${MODULES.USER}/registerUser`, payload)
        .then((responseData) => {
          if (responseData?.programmes?.some((prog) => prog.plan_id)) {
            const error = "Error: You already have an account setup.";
            this.trackUserError({ message: error });

            return;
          }

          dispatch(`${MODULES.PAYMENT}/getSaleSummaryAction`, {
            email: payload.email,
            paymentType: payload.paymentType,
            ...denormaliseOrderSummary(this.formData),
            voucher_code: this.formData.promoApplied.value,
          })
            .then(() => this.identifyNewUserEvent(responseData))
            .then(() => this.action(this.formData.email.value))
            .then(() => (this.registerUserIsLoading = false));
        })
        .catch((e) => {
          this.registerUserIsLoading = false;
          this.trackUserError(e);
        });
    },
    trackEmail() {
      if (window?.analytics) {
        window.analytics.track("New user - Ask for email", {
          courseType: getCourseType(this.onboardingData.choose_path),
          Host: "Web",
        });
      }
    },
    trackUserError(error) {
      this.errorMessage = error?.message || error?.error;
      this.registerUserIsLoading = false;

      if (window?.analytics && window?.analytics?.track) {
        window.analytics.track("New user - Has an account already", {
          Host: "Web",
          type: error?.message || error?.error,
        });
      }
    },
    identifyNewUserEvent(responseData) {
      if (
        responseData?.id &&
        window?.analytics &&
        window?.analytics?.identify &&
        window?.analytics?.track
      ) {
        const { plan_id } = this.formData;
        const { email, id } = responseData;

        const planInfo = this.getPlans().find(
          (plan) => plan_id.value === plan.id
        );

        window.analytics.identify(
          id,
          {
            email,
            subscriptionPeriod: planInfo?.billing_units,
            currency: (planInfo.first_month_price || planInfo.price).name,
          },
          {},
          () => {
            window.analytics.track("New User - Account Created", {
              Host: "Web",
              subscriptionPeriod: planInfo?.billing_units,
              currency: (planInfo.first_month_price || planInfo.price).name,
              planValue: (planInfo.first_month_price || planInfo.price).amount,
            });

            this.updateFormState("user_id", id, false);
          }
        );
      }
    },
  },
};
</script>
